import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  requestForgotPasswordOtp,
  verifyForgotPasswordOtp,
} from '../api/passwordRecoveryApi'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const OTP_LENGTH = 6
const OTP_TIMER_SECONDS = 119

function normalizeEmail(value) {
  return (value || '').trim().toLowerCase()
}

export function useOtpVerificationScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const inputRefs = useRef(Array.from({ length: OTP_LENGTH }))

  const initialTenantUsername = (
    location.state?.tenantUsername ||
    localStorage.getItem('rememberTenantUsername') ||
    ''
  ).trim().toUpperCase()
  const initialEmail = normalizeEmail(location.state?.email)
  const initialMessage = location.state?.message || ''
  const [tenantUsername] = useState(initialTenantUsername)
  const [email] = useState(initialEmail)
  const [digits, setDigits] = useState(Array.from({ length: OTP_LENGTH }, () => ''))
  const [timerSeconds, setTimerSeconds] = useState(OTP_TIMER_SECONDS)
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState(initialMessage)

  const otpCode = useMemo(() => digits.join(''), [digits])
  const isEmailValid = useMemo(() => EMAIL_REGEX.test(normalizeEmail(email)), [email])

  useEffect(() => {
    if (timerSeconds <= 0) {
      return undefined
    }
    const timer = window.setInterval(() => {
      setTimerSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [timerSeconds])

  useEffect(() => {
    if (!initialEmail || !EMAIL_REGEX.test(initialEmail)) {
      navigate('/login', { replace: true })
    }
  }, [initialEmail, navigate])

  const formattedTimer = useMemo(() => {
    const minutes = String(Math.floor(timerSeconds / 60)).padStart(2, '0')
    const seconds = String(timerSeconds % 60).padStart(2, '0')
    return `${minutes}:${seconds}`
  }, [timerSeconds])

  async function handleResendCode(optionalEmail) {
    const targetEmail = normalizeEmail(optionalEmail ?? email)
    setError('')
    setMessage('')

    if (!EMAIL_REGEX.test(targetEmail)) {
      setError('Enter a valid official email to receive a code.')
      return
    }

    setIsSendingOtp(true)
    try {
      await requestForgotPasswordOtp({ tenantUsername, email: targetEmail })
      setTimerSeconds(OTP_TIMER_SECONDS)
      setMessage('Verification code sent. Check your inbox.')
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError, 'Unable to send code right now. Please try again.')
      )
    } finally {
      setIsSendingOtp(false)
    }
  }

  function handleDigitChange(index, rawValue) {
    const value = rawValue.replace(/\D/g, '')
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value.slice(-1)
      return next
    })

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  function handleDigitKeyDown(index, event) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handleDigitPaste(event) {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) {
      return
    }

    event.preventDefault()
    const nextDigits = Array.from({ length: OTP_LENGTH }, (_, idx) => pasted[idx] || '')
    setDigits(nextDigits)

    const nextFocus = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[nextFocus]?.focus()
  }

  async function handleVerifyOtp(event) {
    event.preventDefault()
    setError('')
    setMessage('')

    const normalizedEmail = normalizeEmail(email)
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError('Enter a valid official email address.')
      return
    }

    if (otpCode.length !== OTP_LENGTH || /\D/.test(otpCode)) {
      setError('Enter the 6-digit verification code.')
      return
    }

    setIsVerifyingOtp(true)
    try {
      await verifyForgotPasswordOtp({ tenantUsername, email: normalizedEmail, otp: otpCode })
      navigate('/auth/password-reset', {
        replace: true,
        state: {
          tenantUsername,
          email: normalizedEmail,
          otp: otpCode,
          mode: 'forgot-password',
        },
      })
    } catch (requestError) {
      setError(
        extractApiErrorMessage(requestError, 'Invalid or expired OTP. Request a new code and retry.')
      )
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  return {
    email,
    digits,
    timerSeconds,
    formattedTimer,
    isEmailValid,
    isSendingOtp,
    isVerifyingOtp,
    error,
    message,
    inputRefs,
    handleDigitChange,
    handleDigitKeyDown,
    handleDigitPaste,
    handleResendCode,
    handleVerifyOtp,
  }
}
